/** First-meeting-reward 45-day-midpoint reminder (CHP-387).
 *
 * A LIVE deal that has passed its 45-day midpoint with no claimed first-meeting
 * reward surfaces a "First meeting reward status" card:
 *   - to the registering BDM on a DIRECT-payout firm, on their deals dashboard
 *     (footer: "Claim reward" | "Dismiss"); and
 *   - to the Programme Owner on a POOLED-payout firm, on the Main Dashboard feed
 *     for ANY org deal (footer: "Remind me in 14 days" | "Stop reminders").
 *
 * The predicate is shared by both surfaces so "is this deal due a midpoint
 * reminder?" is decided in exactly one place. The per-viewer payout gate
 * (direct vs pooled) is applied by the caller — this module only answers the
 * deal-level question.
 */

/** Days from go-live before the midpoint reminder fires. The deal is "45 days
 * into a live deal" / "passed the 45-day midpoint" (CHP-387). */
export const MEETING_REWARD_MIDPOINT_DAYS = 45;

/** The deal-level fields the midpoint predicate reads. Both `ManagerDealListItem`
 * (PO team feed) and `DealListItem` (BDM personal feed) carry these (optional on
 * each, populated by the list endpoints) — so the predicate accepts the common
 * subset rather than either concrete shape. */
export interface MeetingRewardReminderInput {
  deal_status: string;
  /** Went-live time (auto_approved_at ?? reviewed_at) — the midpoint origin. */
  live_since?: string | null;
  /** Latest meeting-reward claim status; null/undefined when none exists. */
  meeting_reward_status?: string | null;
  meeting_reward_reminder_snoozed_until?: string | null;
  meeting_reward_reminder_stopped_at?: string | null;
}

/**
 * Whether a deal should surface the first-meeting-reward midpoint reminder.
 *
 * Fires when ALL hold:
 *   - the deal is LIVE (the reward is a live-deal action);
 *   - it has a `live_since` ≥ 45 days in the past (past the midpoint);
 *   - NO reward is claimed/in-flight — `meeting_reward_status` is null/absent or
 *     "rejected" (a pending or approved claim suppresses the nudge); and
 *   - the reminder isn't stopped, and isn't snoozed into the future.
 *
 * Returns false on any missing/unparseable timestamp — a reminder we can't
 * place on the timeline is safer suppressed than shown against bad data.
 */
export function isMeetingRewardReminderDue(item: MeetingRewardReminderInput, now: Date): boolean {
  if (item.deal_status !== "live") return false;
  if (item.meeting_reward_reminder_stopped_at) return false;

  // A pending or approved claim means the reward is already claimed/in-flight —
  // only an unclaimed (null/absent) or previously-rejected deal needs the nudge.
  const status = item.meeting_reward_status;
  if (status === "pending" || status === "approved") return false;

  const snoozedUntil = item.meeting_reward_reminder_snoozed_until;
  if (snoozedUntil) {
    const snoozeMs = Date.parse(snoozedUntil);
    if (!Number.isNaN(snoozeMs) && snoozeMs > now.getTime()) return false;
  }

  if (!item.live_since) return false;
  const liveMs = Date.parse(item.live_since);
  if (Number.isNaN(liveMs)) return false;
  const daysLive = (now.getTime() - liveMs) / 86_400_000;
  return daysLive >= MEETING_REWARD_MIDPOINT_DAYS;
}
